import React, { useState, useCallback, useMemo } from "react";
import { IProductionDashboardStepDTO } from "../../models/DTO/Production/IProductionResponseDTO";
import IUser from "../../models/IUser";
import SerialSelectionList from "./SerialSelectionList";
import AdvanceStepModal from "./AdvanceStepModal";
import * as ProductionService from "../../services/ProductionService";
import { ProductionAuthContext } from "../../services/ProductionService";
import NotificationLevelEnum from "../../models/NotificationLevelEnum";
import PartialShipBehaviorEnum from "../../models/PartialShipBehaviorEnum";
import NotificationPositionEnum from "../../models/NotificationPositionEnum";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";

interface ShopFloorStepViewProps {
    steps: IProductionDashboardStepDTO[];
    activeStep: IProductionDashboardStepDTO | null;
    onSelectStep: (step: IProductionDashboardStepDTO) => void;
    serials: any[];
    totalCount: number;
    page: number;
    pageSize: number;
    isSerialsLoading: boolean;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    searchValue: string;
    onSearchChange: (value: string) => void;
    employees: IUser[];
    selectedEmployeeId: number | null;
    isAdmin: boolean;
    ctx: ProductionAuthContext;
    refreshData: () => Promise<void>;
    notifyFn: (obj: any, notification: any) => void;
    isCompact: boolean;
}

const ShopFloorStepView: React.FC<ShopFloorStepViewProps> = ({
    steps,
    activeStep,
    onSelectStep,
    serials,
    totalCount,
    page,
    pageSize,
    isSerialsLoading,
    onPageChange,
    onPageSizeChange,
    searchValue,
    onSearchChange,
    employees,
    selectedEmployeeId,
    isAdmin,
    ctx,
    refreshData,
    notifyFn,
    isCompact,
}) => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [showAdvanceModal, setShowAdvanceModal] = useState(false);
    const [isAdvancing, setIsAdvancing] = useState(false);
    const [advanceError, setAdvanceError] = useState<string | null>(null);

    const isReleaseTerminalStep =
        activeStep?.is_terminal === true && ctx.supplierModel.partial_ship_behavior === PartialShipBehaviorEnum.RELEASE_FOR_SHIPMENT;

    const nextStep = useMemo((): IProductionDashboardStepDTO | null => {
        if (!activeStep || steps.length === 0) return null;
        const currentIndex = steps.findIndex((s) => s.stepId === activeStep.stepId);
        if (currentIndex < 0 || currentIndex >= steps.length - 1) return null;
        return steps[currentIndex + 1];
    }, [activeStep, steps]);

    const handleToggleSerial = useCallback((id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const handleToggleSelectAll = useCallback(() => {
        const getKey = (s: any) => String(s.id);
        const allSelected = serials.length > 0 && serials.every((s) => selectedIds.has(getKey(s)));
        if (allSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(serials.map(getKey)));
        }
    }, [serials, selectedIds]);

    const handleSelectRange = useCallback((ids: string[]) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            for (const id of ids) {
                next.add(id);
            }
            return next;
        });
    }, []);

    const handleUnselectAll = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    const handleSelectStep = useCallback(
        (step: IProductionDashboardStepDTO) => {
            onSelectStep(step);
            setSelectedIds(new Set());
            onPageChange(0);
        },
        [onSelectStep, onPageChange]
    );

    const handleAdvanceConfirm = async (employeeUserId: number | undefined, machineStation: string, notes: string) => {
        if (!nextStep?.stepId) return;

        setIsAdvancing(true);
        setAdvanceError(null);
        try {
            const result = await ProductionService.advanceSerials(ctx, {
                serial_ids: Array.from(selectedIds).map(Number),
                serial_workflow_step_id: nextStep.stepId,
                employee_user_id: employeeUserId,
                machine_station: machineStation || undefined,
                notes: notes || undefined,
            });

            notifyFn(null, {
                message: `${result.advanced} serial${result.advanced !== 1 ? "s" : ""} advanced to ${nextStep.stepName}`,
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
                autoDismiss: 4,
            });

            if (result.errors?.length > 0) {
                notifyFn(null, {
                    message: `Errors: ${result.errors.join(", ")}`,
                    level: NotificationLevelEnum.error,
                    position: NotificationPositionEnum.bc,
                    autoDismiss: 8,
                });
            }

            setSelectedIds(new Set());
            setShowAdvanceModal(false);
            await refreshData();
        } catch (err: any) {
            setAdvanceError(err.message || "Failed to advance serials");
        } finally {
            setIsAdvancing(false);
        }
    };

    return (
        <div className="sf-step-view">
            {/* Step selector: dropdown on phone, tabs on tablet */}
            {isCompact ? (
                <div className="sf-step-view__dropdown-wrap">
                    <select
                        className="form-select sf-step-view__dropdown"
                        value={activeStep?.stepId ?? ""}
                        onChange={(e) => {
                            const step = steps.find((s) => s.stepId === Number(e.target.value));
                            if (step) handleSelectStep(step);
                        }}>
                        {steps.map((step) => (
                            <option key={step.stepId} value={step.stepId ?? ""}>
                                {step.stepName} ({step.serialCount})
                            </option>
                        ))}
                    </select>
                </div>
            ) : (
                <div className="sf-step-view__tabs">
                    {steps.map((step) => (
                        <button
                            key={step.stepId}
                            className={`sf-step-view__tab ${activeStep?.stepId === step.stepId ? "sf-step-view__tab--active" : ""}`}
                            onClick={() => handleSelectStep(step)}>
                            <span className="sf-step-view__tab-name">
                                <OwTranslate id={step.stepName} defaultMessage={step.stepName} />
                            </span>
                            <span className="sf-step-view__tab-count">{step.serialCount}</span>
                        </button>
                    ))}
                </div>
            )}

            <SerialSelectionList
                serials={serials}
                selectedIds={selectedIds}
                totalCount={totalCount}
                page={page}
                pageSize={pageSize}
                isLoading={isSerialsLoading}
                onToggleSerial={handleToggleSerial}
                onToggleSelectAll={handleToggleSelectAll}
                onSelectRange={handleSelectRange}
                onUnselectAll={handleUnselectAll}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                searchValue={searchValue}
                onSearchChange={onSearchChange}
                selectionDisabled={isReleaseTerminalStep}
            />

            <div className="sf-step-view__action-bar">
                {isReleaseTerminalStep ? (
                    <div className="sf-step-view__release-note">
                        <OwTranslate id="serials_removed_on_shipment_release" defaultMessage="Serials are removed on Shipment Release" />
                    </div>
                ) : (
                    <button
                        className="btn btn-success sf-step-view__advance-btn"
                        disabled={selectedIds.size === 0 || !nextStep}
                        onClick={() => {
                            if (selectedIds.size > 0) setShowAdvanceModal(true);
                        }}>
                        <OwTranslate id="advance_selected" defaultMessage="Advance Selected" /> ({selectedIds.size})
                        {nextStep && (
                            <span className="sf-step-view__advance-target">
                                {" "}
                                → <OwTranslate id={nextStep.stepName} defaultMessage={nextStep.stepName} />
                            </span>
                        )}
                    </button>
                )}
            </div>

            <AdvanceStepModal
                show={showAdvanceModal}
                selectedCount={selectedIds.size}
                currentStep={activeStep}
                nextStep={nextStep}
                employees={employees}
                defaultEmployeeId={selectedEmployeeId}
                isAdmin={isAdmin}
                isAdvancing={isAdvancing}
                errorMessage={advanceError}
                machineStations={ctx.supplierModel.settings?.screens?.shop_floor?.machine_stations}
                onConfirm={handleAdvanceConfirm}
                onCancel={() => {
                    setShowAdvanceModal(false);
                    setAdvanceError(null);
                }}
            />
        </div>
    );
};

export default ShopFloorStepView;
